import { useState } from 'react'
import { CfoBrandMark, navigateTo } from '../cfo-site/CfoSiteShared'
import { BOOK_WORKING_SESSION_URL, connectApi, copyText, type CapabilityResponse } from '../connect/connectApi'
import { FgHelp } from '../help/FgHelp'
import { PLAYBOOKS, firstCompanyName, playbookById, renderPrompt, renderScript, type Playbook } from './playbooks'
import '../connect/ConnectPage.css'

type PracticeApi = Pick<typeof connectApi, 'demoSessionToken' | 'invoke' | 'markStep'>

/** A practice run: a demo session held in memory and the demo company it reached. */
type Practice = { token: string; company: string }
type StepResult = { state: 'running' } | { state: 'done'; response: CapabilityResponse } | { state: 'failed'; message: string }


function describe(response: CapabilityResponse): string {
  if (response.proposed_call) {
    return `FuseGrid would ask for your approval to run ${response.proposed_call.name}. Practice mode never makes changes.`
  }
  if (!response.ok) return `FuseGrid answered: ${response.error?.message || response.error?.code || 'an error'}.`
  return JSON.stringify(response.result, null, 2)?.slice(0, 4000) ?? 'Done.'
}

function PlaybookList() {
  return <>
    <header className="fg-connect-hero">
      <h1>Playbooks<FgHelp id="playbooks.page" /></h1>
      <p>Step-by-step ways to do real advisor work with AI. Each step says what the AI does, what you check, the exact prompt and what you should get back. Practise any of them on a demo company first.</p>
    </header>
    <ul className="fg-playbook-list">
      {PLAYBOOKS.map(playbook => <li key={playbook.id} data-playbook={playbook.id}>
        <h2 style={{ margin: '0 0 4px', fontSize: 16 }}><a href={`/playbooks/${playbook.id}`} onClick={event => { event.preventDefault(); navigateTo(`/playbooks/${playbook.id}`) }}>{playbook.title}</a></h2>
        <p className="fg-connect-muted" style={{ margin: 0 }}>{playbook.summary}</p>
        <p style={{ margin: '6px 0 0', fontSize: 12 }}>{playbook.steps.length} steps{playbook.practice_on_demo ? ' · practice on a demo company' : ''}</p>
      </li>)}
    </ul>
  </>
}

function PlaybookDetail({ playbook, api }: { playbook: Playbook; api: PracticeApi }) {
  const [company, setCompany] = useState('')
  const [period, setPeriod] = useState('')
  const [practice, setPractice] = useState<Practice | null>(null)
  const [practiceError, setPracticeError] = useState<string | null>(null)
  const [results, setResults] = useState<Record<number, StepResult>>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [finished, setFinished] = useState(false)
  const next = playbookById(playbook.next)
  const shownCompany = practice?.company || company.trim() || '{company}'
  const fill = (text: string) => (shownCompany === '{company}' && !period.trim() ? text : renderPrompt(text, shownCompany, period))

  const startPractice = async () => {
    setPracticeError(null)
    try {
      const token = await api.demoSessionToken()
      const listed = await api.invoke('list_companies', {}, { token })
      const name = listed.ok ? firstCompanyName(listed.result) : null
      if (!name) throw new Error('no_demo_company')
      setPractice({ token, company: name })
      setResults({})
      setFinished(false)
    } catch {
      setPracticeError('The demo company could not be opened. Try again in a minute.')
    }
  }
  const runStep = async (index: number) => {
    if (!practice) return
    setResults(current => ({ ...current, [index]: { state: 'running' } }))
    try {
      // Practice runs go through ask_fusegrid: reads run on the demo company; changes come back as a proposal.
      const response = await api.invoke('ask_fusegrid', { request: renderPrompt(playbook.steps[index].prompt, practice.company, period), company: practice.company }, { company: practice.company, token: practice.token })
      setResults(current => ({ ...current, [index]: { state: 'done', response } }))
    } catch {
      setResults(current => ({ ...current, [index]: { state: 'failed', message: 'This step could not run. Try it again.' } }))
    }
  }
  const finish = async (step: 'first_playbook_demo' | 'first_playbook_real') => {
    setFinished(true)
    // The advisor's own session records progress; a signed-out visitor simply has no checklist.
    await api.markStep(step, playbook.id).catch(() => undefined)
  }
  const copyAll = async () => {
    setNotice(await copyText(renderScript(playbook, company.trim() || 'my client', period)) ? 'Copied. Paste it into Claude or ChatGPT with FuseGrid connected.' : 'Copy failed.')
  }

  return <>
    <p><a href="/playbooks" onClick={event => { event.preventDefault(); navigateTo('/playbooks') }}>← All playbooks</a></p>
    <header className="fg-connect-hero">
      <h1>{playbook.title}</h1>
      <p>{playbook.summary}</p>
    </header>

    <section className="fg-connect-card" aria-labelledby="fg-run-title">
      <div className="fg-connect-card-head">
        <h2 id="fg-run-title">Run it<FgHelp id="playbooks.run" /></h2>
        <p>Name the client and month, then copy the whole playbook into Claude or ChatGPT, or ask for it by name: “Start the {playbook.title} playbook for …”.</p>
      </div>
      <div className="fg-ask-filters">
        <label>Client<input type="search" value={company} onChange={event => setCompany(event.target.value)} placeholder="e.g. Cedar & Oak" /></label>
        <label>Month<input type="search" value={period} onChange={event => setPeriod(event.target.value)} placeholder="latest closed month" /></label>
        <button type="button" onClick={() => void copyAll()}>Copy the whole playbook</button>
        <button type="button" onClick={() => void finish('first_playbook_real')}>I ran this on a real client</button>
      </div>
      {notice && <p className="fg-connect-notice" role="status">{notice}</p>}
    </section>

    {playbook.practice_on_demo && <section className="fg-connect-card" aria-labelledby="fg-practice-title">
      <div className="fg-connect-card-head">
        <h2 id="fg-practice-title">Practice mode<FgHelp id="playbooks.practice" /></h2>
        <p>Run each step against a synthetic demo company. Nothing you do here touches a real client, and changes are only ever proposed.</p>
      </div>
      {!practice && <button type="button" onClick={() => void startPractice()}>Practice on a demo company</button>}
      {practice && <p>Practising on <strong>{practice.company}</strong>.</p>}
      {practiceError && <p className="fg-connect-error" role="alert">{practiceError}</p>}
    </section>}

    <ol className="fg-playbook-steps" style={{ display: 'grid', gap: 12 }}>
      {playbook.steps.map((step, index) => {
        const result = results[index]
        return <li className="fg-playbook-step" key={step.title} data-capability={step.capability}>
          <h3>Step {index + 1}: {step.title}</h3>
          <dl>
            <dt>Prompt</dt><dd><q>{fill(step.prompt)}</q> <button type="button" onClick={async () => setNotice(await copyText(fill(step.prompt)) ? 'Prompt copied.' : 'Copy failed.')}>Copy</button></dd>
            <dt>FuseGrid returns</dt><dd>{fill(step.returns)}</dd>
            <dt>The assistant then</dt><dd>{fill(step.assistant_then)}</dd>
            <dt>What you check</dt><dd>{fill(step.what_you_check)}</dd>
            <dt>Why it matters</dt><dd>{step.why}</dd>
          </dl>
          {practice && <p style={{ margin: '10px 0 0' }}>
            <button type="button" disabled={result?.state === 'running'} onClick={() => void runStep(index)}>{result?.state === 'running' ? 'Running…' : result ? 'Run again' : 'Run this step'}</button>
          </p>}
          {result?.state === 'done' && <pre className="fg-practice-result" data-testid={`practice-result-${index}`}>{describe(result.response)}</pre>}
          {result?.state === 'failed' && <p className="fg-connect-error" role="alert">{result.message}</p>}
        </li>
      })}
    </ol>

    {practice && !finished && <p><button type="button" onClick={() => void finish('first_playbook_demo')}>Finish practice</button></p>}
    {finished && <section className="fg-connect-card" role="status" aria-label="What next">
      <h2 style={{ marginTop: 0, fontSize: 16 }}>Nice work.</h2>
      {next && <p>Next, try <a href={`/playbooks/${next.id}`} onClick={event => { event.preventDefault(); navigateTo(`/playbooks/${next.id}`) }}>{next.title}</a>: {next.summary}</p>}
      <p>Want a hand with a real client? <a href={BOOK_WORKING_SESSION_URL}>Book a working session</a>.</p>
    </section>}
  </>
}

export function PlaybooksPage({ playbookId, api = connectApi }: { playbookId: string | null; api?: PracticeApi }) {
  const playbook = playbookId ? playbookById(playbookId) : undefined
  return <div className="fg-connect">
    <nav className="fg-connect-nav">
      <a href="/" onClick={event => { event.preventDefault(); navigateTo('/') }}><CfoBrandMark sub="Playbooks" /></a>
      <span>
        <a href="/connect">Connect</a>
        <a href="/support">Support</a>
      </span>
    </nav>
    <main className="fg-connect-main">
      {playbookId && !playbook ? <p role="alert">That playbook does not exist. <a href="/playbooks">See all playbooks</a>.</p>
        : playbook ? <PlaybookDetail key={playbook.id} playbook={playbook} api={api} /> : <PlaybookList />}
    </main>
  </div>
}

export default PlaybooksPage
